import "./Admin.css";
import { useState, useEffect, useCallback } from "react";
import logger from "../Components/Logger";
import { auth } from "../Firebase";
import { EditCourseModal } from "./Components/Admin/EditCourseModal";
import { AddCourseModal } from "./Components/Admin/AddCourseModal";
import { EditEventModal } from "./Components/Admin/EditEventModal";
import { CreateEventModal } from "./Components/Admin/CreateEventModal";
import { PricingModal } from "./Components/Admin/PricingModal";

// Admin dashboard component for managing course offerings and pricing, with enhanced error handling,
// logging, and user feedback for a better admin experience.
// Optimized to update local state after CRUD operations instead of refetching from Firestore,
// reducing unnecessary reads and staying within free quota limits.

const COURSES_CACHE_KEY = "a1app_courses_cache";
const PRICING_CACHE_KEY = "a1app_pricing_cache";
const EVENTS_CACHE_KEY = "a1app_events_cache";
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

// ── Group raw API data into Elementary / Highschool / Cegep ──
const groupCourses = (data) => {
  const groupedData = { Elementary: [], Highschool: [], Cegep: [] };
  data.forEach((item) => {
    const level =
      item.level === "High School"
        ? "Highschool"
        : item.level === "College"
          ? "Cegep"
          : item.level;

    const course = {
      id: item.id,
      title: item.Title || item.subject || "Sans titre",
      icon: item.icon || "",
      description: item.description || "",
      status: item.status || item.Status || "active",
      details: {
        description: item.Description || "",
        approach: item.OurApproche || "",
        subjects: item.SubjectsCovered || "",
      },
    };

    if (groupedData[level]) groupedData[level].push(course);
  });
  return groupedData;
};

const pricingCategories = [
  { key: 'primary_group_weekdays', label: 'Primary', note: 'Group weekdays' },
  { key: 'primary_group_weekends', label: 'Primary', note: 'Group weekends', isWeekend: true },
  { key: 'secondary_group_weekdays', label: 'Secondary', note: 'Group weekdays' },
  { key: 'secondary_group_weekends', label: 'Secondary', note: 'Group weekends', isWeekend: true },
  { key: 'primary_1v1', label: 'Primary', note: '1-on-1', noTranslate: true },
  { key: 'secondary_1v1', label: 'Secondary', note: '1-on-1', noTranslate: true },
  { key: 'college_1v1', label: 'Cegep', note: '1-on-1', noTranslate: true },
];

//aider par l'ia pour ajouter une sécurité de plus au fonction
const getAuthHeaders = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error("Utilisateur non connecté");

  const token = await user.getIdToken();
  return {
    "Content-Type": "application/json",
    "Authorization": `Bearer ${token}`
  };
}

export function Admin({ t }) {
  const [activePageAdm, setActivePageAdm] = useState("Elementary");
  const [selectedCourseAdmin, setSelectedCourseAdmin] = useState(null);
  const [selectedEventAdmin, setSelectedEventAdmin] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [searchItem, setSearchItem] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [minEndDate, setMinEndDate] = useState("");

  const [courseData, setCourseData] = useState({
    Elementary: [],
    Highschool: [],
    Cegep: [],
  });

  const [events, setEvents] = useState([]);

  const [prices, setPrices] = useState({});
  const pricingCount = Object.keys(prices).length;

  const levelDisplay = {
    Elementary: "Primary",
    Highschool: "Secondary",
    Cegep: "Cegep",
  };

  const ClassLevel = ["Elementary", "Highschool", "Cegep"];

  const filteredCourses = courseData[activePageAdm].filter((coursesItem) =>
    coursesItem.title.toLowerCase().includes(searchItem.toLowerCase()),
  );

  const preventMinus = (e) => {
    if (e.code === "Minus" || e.key === "-") {
      e.preventDefault();
      logger.warn("Negative values are not allowed in pricing inputs.");
    }
  };

  const getApiLevel = (level) => {
    if (level === "Highschool") return "High School";
    if (level === "Cegep") return "College";
    return level;
  };

  const handleResponse = async (res) => {
    if (!res.ok) {
      logger.error(`HTTP error! Status: ${res.status}`);
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || `Erreur HTTP: ${res.status}`);
    }
    return res.json();
  };

  // ── Fetch courses with localStorage cache (1 hour TTL) ──
  const fetchCourses = useCallback(async () => {
    try {
      const cached = localStorage.getItem(COURSES_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
          logger.info("Courses loaded from localStorage cache.");
          setCourseData(groupCourses(data));
          return;
        }
      }
    } catch (_) {}

    // Cache miss or expired → fetch from Firestore
    const res = await fetch("/.netlify/functions/getAllCourses");
    const data = await handleResponse(res);

    try {
      localStorage.setItem(COURSES_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
    } catch (_) {}

    logger.info("Courses fetched from Firestore and cached.");
    setCourseData(groupCourses(data));
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const cached = localStorage.getItem(EVENTS_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
          logger.info("Events loaded from localStorage cache.");
          setEvents(data);
          return;
        }
      }
    } catch (_) {}

    const res = await fetch("/.netlify/functions/getAllEvents");
    const data = await handleResponse(res);

    try{
      localStorage.setItem(EVENTS_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
    } catch (_) {}

    logger.info("Events fetched from Firestore and cached.");
    setEvents(data);
  }, []);


  // ── Fetch pricing with localStorage cache (1 hour TTL) ──
  const fetchPrices = useCallback(async () => {
    try {
      const cached = localStorage.getItem(PRICING_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
          logger.info("Pricing loaded from localStorage cache.");
          setPrices(data);
          return;
        }
      }
    } catch (_) {}

    const res = await fetch("/.netlify/functions/getPricing");
    const data = await handleResponse(res);

    try {
      localStorage.setItem(PRICING_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
    } catch (_) {}

    logger.info("Pricing fetched from Firestore and cached.");
    setPrices(data);
  }, []);

  // ── Load data once on mount with a single retry if it fails ──
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([fetchCourses(), fetchPrices(), fetchEvents()]);
      } catch (err) {
        logger.error(`First load failed: ${err.message}`);
        try {
          await new Promise((resolve) => setTimeout(resolve, 1200));
          await Promise.all([fetchCourses(), fetchPrices(), fetchEvents()]);
        } catch (retryErr) {
          logger.error(`Retry failed: ${retryErr.message}`);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadData();
    return () => { isMounted = false; };
  }, [fetchCourses, fetchPrices, fetchEvents]);

  // ── Lock body scroll when a modal is open ──
  useEffect(() => {
    const shouldLock = selectedCourseAdmin || isAddModalOpen || isPricingModalOpen || selectedEventAdmin || isEventModalOpen;
    document.body.style.overflow = shouldLock ? "hidden" : "auto";
    return () => { document.body.style.overflow = "auto"; };
  }, [selectedCourseAdmin, isAddModalOpen, isPricingModalOpen, selectedEventAdmin, isEventModalOpen]);

  // ── Delete: remove from local state + invalidate cache ──
  const handleDelete = async (course) => {
    if (!window.confirm(`Are you sure you want to delete the course "${course.title}"?`)) return;

    logger.info(`Deleting course "${course.title}" (ID: ${course.id}).`);

    fetch("/.netlify/functions/deleteCourseSubject", {
      method: "DELETE",
      headers: await getAuthHeaders(),
      body: JSON.stringify({ id: course.id, level: getApiLevel(activePageAdm) }),
    })
      .then(handleResponse)
      .then(() => {
        setCourseData((prev) => ({
          ...prev,
          [activePageAdm]: prev[activePageAdm].filter((c) => c.id !== course.id),
        }));
        // ✅ Invalidate cache so visitors see the change on next visit
        try { localStorage.removeItem(COURSES_CACHE_KEY); } catch (_) {}
        logger.info(`Course "${course.title}" deleted and cache invalidated.`);
      })
      .catch((err) => {
        logger.error(`Error deleting course: ${err.message}`);
      });
  };

  // ── Update: patch local state + invalidate cache ──
  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const formData = new FormData(e.target);

    const updatedFields = {
      title: formData.get("title").trim(),
      icon: formData.get("icon").trim(),
      status: formData.get("status"),
      details: {
        description: formData.get("description").trim(),
        approach: formData.get("approach").trim(),
        subjects: formData.get("subjects").trim(),
      },
    };

    try {
      const response = await fetch("/.netlify/functions/updateCourseSubject", {
        method: "PUT",
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          id: selectedCourseAdmin.id,
          level: getApiLevel(activePageAdm),
          Title: updatedFields.title,
          subject: updatedFields.title,
          icon: updatedFields.icon,
          OurApproche: updatedFields.details.approach,
          SubjectsCovered: updatedFields.details.subjects,
          status: updatedFields.status,
          Description: updatedFields.details.description,
        }),
      });
      await handleResponse(response);

      setCourseData((prev) => ({
        ...prev,
        [activePageAdm]: prev[activePageAdm].map((c) =>
          c.id === selectedCourseAdmin.id ? { ...c, ...updatedFields } : c
        ),
      }));

      // ✅ Invalidate cache so visitors see the change on next visit
      try { localStorage.removeItem(COURSES_CACHE_KEY); } catch (_) {}

      setSelectedCourseAdmin(null);
      logger.info(`Course "${updatedFields.title}" updated and cache invalidated.`);
    } catch (err) {
      logger.error(`Failed to update course: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Create: append to local state + invalidate cache ──
  const handleCreateCourse = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const formData = new FormData(e.target);
    const selectedLevel = formData.get("level");

    try {
      const response = await fetch("/.netlify/functions/createCourseSubject", {
        method: "POST",
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          level: getApiLevel(selectedLevel),
          Title: formData.get("title"),
          subject: formData.get("title"),
          icon: formData.get("icon"),
          OurApproche: formData.get("approach"),
          SubjectsCovered: formData.get("subjects"),
          status: formData.get("status"),
          Description: formData.get("description"),
        }),
      });

      const created = await handleResponse(response);

      const newCourse = {
        id: created?.id || `temp_${Date.now()}`,
        title: formData.get("title"),
        icon: formData.get("icon") || "",
        status: formData.get("status"),
        details: {
          description: formData.get("description"),
          approach: formData.get("approach"),
          subjects: formData.get("subjects"),
        },
      };

      setCourseData((prev) => ({
        ...prev,
        [selectedLevel]: [...prev[selectedLevel], newCourse],
      }));

      // ✅ Invalidate cache so visitors see the new course on next visit
      try { localStorage.removeItem(COURSES_CACHE_KEY); } catch (_) {}

      setIsAddModalOpen(false);
      logger.info(`Course "${newCourse.title}" created and cache invalidated.`);
    } catch (err) {
      alert("Erreur de création");
      logger.error(`Failed to create course: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Event Section ──
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    const formData = new FormData(e.target);

    try{
      const createResponseEvent = await fetch("/.netlify/functions/createEvent", {
        method: "POST",
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          title: formData.get("title").trim(),
          description: formData.get("description").trim(),
          startDate: formData.get("startDate"),
          endDate: formData.get("endDate"),
          startTime: formData.get("startTime"),
          endTime: formData.get("endTime"),
          address: formData.get("address")?.trim() || "",
          locationName: formData.get("locationName")?.trim() || "",
        }),
      });

      const createEventData = await handleResponse(createResponseEvent);

      const newEvent = {
        id: createEventData?.id || `temp_${Date.now()}`,
        title: formData.get("title").trim(),
        description: formData.get("description").trim(),
        startDate: formData.get("startDate"),
        endDate: formData.get("endDate"),
        startTime: formData.get("startTime"),
        endTime: formData.get("endTime"),
        address: formData.get("address")?.trim() || "",
        locationName: formData.get("locationName")?.trim() || "",
      };

      setEvents((prev) => [...prev, newEvent]);
      try{
        localStorage.removeItem(EVENTS_CACHE_KEY);
      } catch (_) {}

      setIsEventModalOpen(false);
      logger.info(`Event "${newEvent.title}" created successfully.`);
    }catch (err) {
      logger.error(`Failed to create event: ${err.message}`);
      alert("Error creating event: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

    const handleDeleteEvent = async (eventItem) => {
    if (!window.confirm(`Are you sure you want to delete the event "${eventItem.title}"?`)) return;
    logger.info(`Deleting event "${eventItem.title}" (ID: ${eventItem.id}).`);

    fetch("/.netlify/functions/deleteEvent", {
      method: "DELETE",
      headers: await getAuthHeaders(),
      body: JSON.stringify({ id: eventItem.id }),
    })
      .then(handleResponse)
      .then(() => {
        setEvents((prevEvents) => prevEvents.filter((e) => e.id !== eventItem.id));
        try{
          localStorage.removeItem(EVENTS_CACHE_KEY);
        }catch(_){}

        logger.info(`Event "${eventItem.title}" deleted and cache invalidated.`);
      })
      .catch((err) => {
        logger.error(`Error deleting event: ${err.message}`);
        alert("Failed to delete the event. Please try again.");
      });
    };

    const handleUpdateEvent = async (e) => {
  e.preventDefault();
  setIsSaving(true);

  const formData = new FormData(e.target);
  
  const updatedFields = {
    id: selectedEventAdmin.id, 
    title: formData.get("title").trim(),
    description: formData.get("description").trim(),
    startDate: formData.get("startDate"),
    endDate: formData.get("endDate"),
    startTime: formData.get("startTime"),
    endTime: formData.get("endTime"),
    address: formData.get("address")?.trim() || "",
    locationName: formData.get("locationName")?.trim() || "",
  };
  
  try {
    const response = await fetch("/.netlify/functions/updateEvent", {
      method: "PUT",
      headers: await getAuthHeaders(),
      body: JSON.stringify(updatedFields),
    });

    await handleResponse(response);
    setEvents((prev) => 
      prev.map((ev) => 
        ev.id === updatedFields.id ? { ...ev, ...updatedFields } : ev
      )
    );
    try {
      localStorage.removeItem(EVENTS_CACHE_KEY);
    } catch (_) {}

    setSelectedEventAdmin(null);
    logger.info(`Event "${updatedFields.title}" updated and cache invalidated.`);
  } catch (err) {
    logger.error(`Failed to update event: ${err.message}`);
    alert("Error updating event.");
  } finally {
    setIsSaving(false);
  }
};

  // ── Pricing update: update local state + refresh cache ──
  const handleUpdatePricing = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const formData = new FormData(e.target);

    const updatedPrices = {
      primary_group_weekdays: Number(formData.get("primary_group_weekdays")),
      primary_group_weekends: Number(formData.get("primary_group_weekends")),
      primary_1v1: Number(formData.get("primary_1v1")),
      secondary_group_weekdays: Number(formData.get("secondary_group_weekdays")),
      secondary_group_weekends: Number(formData.get("secondary_group_weekends")),
      secondary_1v1: Number(formData.get("secondary_1v1")),
      college_1v1: Number(formData.get("college_1v1")),
    };

    try {
      const response = await fetch("/.netlify/functions/updatePricing", {
        method: "PUT",
        headers: await getAuthHeaders(),
        body: JSON.stringify(updatedPrices),
      });
      await handleResponse(response);

      setPrices(updatedPrices);

      // ✅ Overwrite pricing cache with new values immediately
      try {
        localStorage.setItem(PRICING_CACHE_KEY, JSON.stringify({ data: updatedPrices, timestamp: Date.now() }));
      } catch (_) {}

      setIsPricingModalOpen(false);
      logger.info("Pricing updated in local state and cache.");
    } catch (err) {
      logger.error(`Failed to update pricing: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-container">
      <main className="main-content">
        <div className="stats-grid">
                    <div className="stat-card">
            <span className="icon">💰</span>
            <h3>Pricing Entries</h3>
            <div className="stat-value">
              {isLoading ? <span key="loading-p">...</span> : <span key="value-p">{pricingCount}</span>}
            </div>
            <p>Active service prices</p>
          </div>
          <div className="stat-card">
            <span className="icon">📚</span>
            <h3>Primary Courses</h3>
            <div className="stat-value">
              {isLoading ? <span key="loading-e">...</span> : <span key="value-e">{courseData.Elementary.length}</span>}
            </div>
            <p>Available courses</p>
          </div>
          <div className="stat-card">
            <span className="icon">🎓</span>
            <h3>Secondary Courses</h3>
            <div className="stat-value">
              {isLoading ? <span key="loading-h">...</span> : <span key="value-h">{courseData.Highschool.length}</span>}
            </div>
            <p>Available courses</p>
          </div>
          <div className="stat-card">
            <span className="icon">🏫</span>
            <h3>Cegep Courses</h3>
            <div className="stat-value">
              {isLoading ? <span key="loading-c">...</span> : <span key="value-c">{courseData.Cegep.length}</span>}
            </div>
            <p>Available courses</p>
          </div>
        </div>
          

        <div className="content-header">
          <div className="title-group">
            <div className="title-group-admin">
              <h2>Admin Dashboard</h2>
            </div>
            <p>Manage pricing and courses efficiently</p>
          </div>
          <div className="action-buttons">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search a course ..."
                value={searchItem}
                onChange={(e) => setSearchItem(e.target.value)}
              />
            </div>
            <button className="btn btn-Edit" onClick={() => setIsPricingModalOpen(true)}>
              <span>Edit Pricing</span>
            </button>
            <button className="btn btn-Add" onClick={() => setIsAddModalOpen(true)}>
              <span>+ Add Course</span>
            </button>
          </div>
        </div>

        <div className="section-card">
          <h3 className="section-titleAdm">Service Pricing</h3>
          <div className="pricing-grid">
            {pricingCategories.map((item) => 
            <div className="pricing-card" key={item.key}>
              <div className="pricing-info">
                <span> {item.label} </span>
                <small className={`pricing-note ${item.isWeekend ? 'weekend-text' : ''}`} translate={item.noTranslate ? "no" : "yes"}>{item.note}</small>
              </div>
              <strong><span>{prices[item.key]}</span>$</strong>
            </div>
          )}
          </div>
        </div>

        <div className="section-card">
          <div className="section-header-admin">
            <h3 className="section-titleAdm">Course Management</h3>
            <div className="admin-tabs">
              {ClassLevel.map((level) => (
                <button
                  key={level}
                  className={`tab-btn ${activePageAdm === level ? "active" : ""}`}
                  onClick={() => setActivePageAdm(level)}
                >
                  <span>{levelDisplay[level]}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="table-container">
            <table className="data_table">
              <thead>
                <tr>
                  <th>Icons</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Approach</th>
                  <th>Subjects Covered</th>
                  <th>Status</th>
                  <th translate="no">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCourses.map((course) => (
                  <tr key={course.id}>
                    <td>
                      <strong>
                        {course.icon && <span style={{ marginRight: "8px" }}>{course.icon}</span>}
                      </strong>
                    </td>
                    <td><strong><span>{course.title}</span></strong></td>
                    <td className="TruncTextdes"><span>{course.details.description}</span></td>
                    <td className="TruncText" title={course.details.approach}>
                      <span>{course.details.approach}</span>
                    </td>
                    <td className="SubjectClass">
                      <div className="BadgeContainer">
                        {course.details.subjects?.split(",").map((s, index) => (
                          <span className="badge" key={index}>{s.trim()}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`status-pill ${course.status === "inactive" ? "status-inactive" : "status-active"}`}>
                        {course.status === "inactive" ? "Inactive" : "Active"}
                      </span>
                    </td>
                    <td>
                      <div className="action-group">
                        <button className="btn-icon btn-edit" onClick={() => setSelectedCourseAdmin(course)}>
                          <span>Edit</span>
                        </button>
                        <button className="btn-icon btn-delete" onClick={() => handleDelete(course)}>
                          <span>Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="section-card">
          <div className="section-header-admin">
            <h3 className="section-titleAdm">Events List</h3>
            <button className="btn btn-Add" onClick={() => setIsEventModalOpen(true)}>
              + Create New Event
            </button>
          </div>
          <div className="events-admin-grid">
            {events.length > 0 ? (
              events.map((event) =>{
                const isPast = (() => {
                if (!event.endDate) return false;
                const endDateTime = new Date(`${event.endDate}T${event.endTime || "23:59"}`);
                return endDateTime < new Date();
                })();
                return (
                <div className="event-admin-card" key={event.id}>
                  <div className="event-card-content">
                    <div className="event-card-header">
                      <h4>{event.title}</h4>
                      <span className={`status-pill ${isPast ? "status-expired" : "status-active"}`}>{isPast ? "Finished" : "Active"}</span></div>
                      <p className="event-card-desc">{event.description}</p>
                      {(event.locationName || event.address) && (
                      <div className="event-admin-location">
                        <p className="location-label">Location:</p>
                        <div className="location-details">
                        <span className="location-text"> <strong>{event.locationName}</strong>{event.address && ` - ${event.address}`}</span>
                        </div></div> )}
                      <p className="event-schedule-title">Event Schedule:</p>
                      <div className="event-card-dates">
                        <div className="event-moment-group">
                        <span className="date-badge">
                          <strong>Start: </strong> <span className="date-value">{event.startDate ? event.startDate.split('T')[0] : "TBD"}</span></span>{/*Aide le l'ia pour corriger le update de date*/}
                          {event.startTime && (
                            <>
                            <span className="time-label">at</span>
                          <span className="date-badge time-badge">{event.startTime}</span>
                          </>
                          )}</div>
                          <div className="event-moment-group">
                        <span className="date-badge">
                          <strong>End: </strong> <span className="date-value">{event.endDate ? event.endDate.split('T')[0] : "TBD"}</span></span>{/*Aide le l'ia pour corriger le update de date*/}
                          {event.endDate && (
                            <>
                            <span className="time-label">at</span>
                          <span className="date-badge time-badge-end">{event.endTime}</span>
                          </>
                          )}</div>
                      </div>
                      <div className="event-card-actions">
                        <button className="btn-icon btn-edit" onClick={() => { setSelectedEventAdmin(event); setMinEndDate(event.startDate); }} >
                          <span>Edit</span>
                        </button>
                        <button className="btn-icon btn-delete" onClick={() => handleDeleteEvent(event)}>
                          <span>Delete</span>
                        </button>
                      </div>
                  </div> 
                </div>
              );
            })
            ) : (
              <div className="no-events">
                <p>No events created yet.</p>
                </div>
            )}
          </div>
        </div>
      </main>

      {/* ── Edit Course Modal ── */}
      {/* exemple de code trouver sur : https://legacy.reactjs.org/docs/components-and-props.html */}
      {selectedCourseAdmin && (
      <EditCourseModal
        course={selectedCourseAdmin}
        isSaving={isSaving}
        onClose={() => setSelectedCourseAdmin(null)}
        onSubmit={handleUpdateCourse}
      />
    )}

      {/* ── Edit Event Modal ── */}
      {selectedEventAdmin && (
      <EditEventModal
        event={selectedEventAdmin}
        isSaving={isSaving}
        minEndDate={minEndDate}
        setMinEndDate={setMinEndDate}
        onClose={() => setSelectedEventAdmin(null)}
        onSubmit={handleUpdateEvent}
      />
    )}

      {/* ── Add Course Modal ── */}
      {isAddModalOpen && (
      <AddCourseModal
        activePageAdm={activePageAdm}
        ClassLevel={ClassLevel}
        levelDisplay={levelDisplay}
        isSaving={isSaving}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleCreateCourse}
      />
    )}

      {/* ── Pricing Modal ── */}
      {isPricingModalOpen && (
      <PricingModal
        prices={prices}
        isSaving={isSaving}
        preventMinus={preventMinus}
        onClose={() => setIsPricingModalOpen(false)}
        onSubmit={handleUpdatePricing}
      />
    )}

      {/* ── Event Modal ── */}
      {isEventModalOpen && (
      <CreateEventModal
        isSaving={isSaving}
        minEndDate={minEndDate}
        setMinEndDate={setMinEndDate}
        onClose={() => setIsEventModalOpen(false)}
        onSubmit={handleCreateEvent}
      />
    )}
    </div>
  );
}